// src/pages/common/NotificationPage.jsx
import { useEffect, useState } from "react";
import {
  Bell, BellRing, Trash2, Eye, ExternalLink,
  RefreshCw, Home, Clock, CheckCircle, ChevronLeft, ChevronRight,
} from "lucide-react";
import { notifications as api } from "../../services/notifications";
import { emit } from "../../utils/notifyBus";
import { injectUserStyles } from "../user/userStyles";

const STYLE_ID = "notif-page-styles";
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .np-wrap { font-family:'DM Sans',sans-serif; }
    .np-action-bar {
      background:#fff; border:1px solid #e8ecf0; border-radius:10px;
      padding:14px 20px; display:flex; align-items:center; justify-content:space-between;
      flex-wrap:wrap; gap:12px; margin-bottom:20px;
    }
    .np-action-left  { display:flex; align-items:center; gap:10px; }
    .np-action-count {
      font-family:'DM Sans',sans-serif; font-size:11px; letter-spacing:.08em;
      color:#6b7280; display:flex; align-items:center; gap:6px;
    }
    /* Tabs */
    .np-tabs { display:flex; gap:4px; background:#f3f4f6; border-radius:10px; padding:4px; margin-bottom:20px; }
    .np-tab {
      flex:1; padding:8px 16px; border:none; border-radius:7px; cursor:pointer;
      font-family:'DM Sans',sans-serif; font-size:13px; font-weight:500;
      background:transparent; color:#6b7280; transition:all 160ms;
      display:flex; align-items:center; justify-content:center; gap:6px;
    }
    .np-tab:hover { color:#0c1118; }
    .np-tab.active { background:#fff; color:#0c1118; box-shadow:0 1px 4px rgba(0,0,0,.1); }
    .np-tab-badge {
      font-family:'DM Sans',sans-serif; font-size:10px; padding:1px 6px;
      border-radius:10px; background:#e5e7eb; color:#6b7280;
    }
    .np-tab.active .np-tab-badge { background:#0c1118; color:#fff; }
    /* Scroll container */
    .np-notif-scroll {
      height:520px; overflow-y:auto; border:1px solid #e8ecf0; border-radius:10px;
      padding:10px; background:#f9fafb;
    }
    .np-notif-scroll::-webkit-scrollbar { width:5px; }
    .np-notif-scroll::-webkit-scrollbar-track { background:transparent; }
    .np-notif-scroll::-webkit-scrollbar-thumb { background:#d1d5db; border-radius:99px; }
    .np-notif-scroll::-webkit-scrollbar-thumb:hover { background:#9ca3af; }
    /* List */
    .np-notif-list { display:flex; flex-direction:column; gap:6px; }
    .np-notif {
      background:#fff; border:1px solid #e8ecf0; border-radius:9px;
      padding:14px 18px; display:flex; gap:14px; align-items:flex-start;
      transition:box-shadow 160ms, border-color 160ms;
    }
    .np-notif:hover { box-shadow:0 2px 12px rgba(0,0,0,.07); }
    .np-notif.unread { background:rgba(249,171,20,.04); border-color:rgba(249,171,20,.25); }
    .np-dot { width:9px; height:9px; border-radius:50%; flex-shrink:0; margin-top:5px; }
    .np-dot.read   { background:#d1d5db; }
    .np-dot.unread { background:#f9ab14; animation:npPulse 2s ease-in-out infinite; }
    @keyframes npPulse { 0%,100%{opacity:1} 50%{opacity:.4} }
    .np-notif-body { flex:1; min-width:0; }
    .np-notif-row  { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; }
    .np-notif-title { font-size:13.5px; font-weight:600; color:#0c1118; margin-bottom:3px; }
    .np-notif-title.read { color:#374151; font-weight:500; }
    .np-notif-text  { font-size:12.5px; line-height:1.5; color:#6b7280; margin-bottom:8px; }
    .np-notif-meta  { display:flex; align-items:center; gap:12px; font-size:11.5px; color:#9ca3af; font-family:'DM Sans',sans-serif; letter-spacing:.04em; }
    .np-notif-actions {
      display:flex; gap:4px; align-items:center; flex-shrink:0;
      opacity:0; transition:opacity 160ms;
    }
    .np-notif:hover .np-notif-actions { opacity:1; }
    .np-act-btn {
      padding:5px; border:none; background:none; cursor:pointer;
      border-radius:6px; display:flex; align-items:center;
      transition:background 140ms;
    }
    .np-act-btn.read-btn  { color:#10b981; }
    .np-act-btn.read-btn:hover  { background:rgba(16,185,129,.1); }
    .np-act-btn.link-btn  { color:#6366f1; }
    .np-act-btn.link-btn:hover  { background:rgba(99,102,241,.1); }
    .np-act-btn.del-btn   { color:#ef4444; }
    .np-act-btn.del-btn:hover   { background:rgba(239,68,68,.08); }
    /* Pagination */
    .np-pagination {
      display:flex; align-items:center; justify-content:space-between;
      flex-wrap:wrap; gap:10px; margin-top:12px;
      padding:12px 16px; background:#fff; border:1px solid #e8ecf0; border-radius:10px;
    }
    .np-page-left  { display:flex; align-items:center; gap:12px; }
    .np-page-info  { font-family:'DM Sans',sans-serif; font-size:11px; color:#6b7280; letter-spacing:.06em; }
    .np-page-size-label { font-family:'DM Sans',sans-serif; font-size:11px; color:#9ca3af; letter-spacing:.06em; }
    .np-page-size-select {
      height:28px; padding:0 8px; border:1px solid #e8ecf0; border-radius:6px;
      font-family:'DM Sans',sans-serif; font-size:11px; color:#374151; background:#fff;
      cursor:pointer; outline:none; transition:border-color 140ms;
    }
    .np-page-size-select:hover, .np-page-size-select:focus { border-color:#f9ab14; }
    .np-page-btns { display:flex; gap:4px; align-items:center; }
    .np-page-btn {
      width:32px; height:32px; border:1px solid #e8ecf0; background:#fff; border-radius:7px;
      cursor:pointer; display:flex; align-items:center; justify-content:center;
      font-family:'DM Sans',sans-serif; font-size:12px; color:#374151;
      transition:all 140ms;
    }
    .np-page-btn:hover:not(:disabled) { border-color:#f9ab14; color:#f9ab14; }
    .np-page-btn:disabled { opacity:.4; cursor:not-allowed; }
    .np-page-btn.active { background:#0c1118; border-color:#0c1118; color:#fff; }
    /* Stats */
    .np-stats-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin-top:20px; }
    @media (max-width:767px) { .np-stats-grid { grid-template-columns:1fr; } }
    .np-stat-card { background:#fff; border:1px solid #e8ecf0; border-radius:10px; padding:16px 18px; display:flex; align-items:center; gap:14px; }
    .np-stat-icon { width:38px; height:38px; border-radius:8px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .np-stat-val { font-family:'DM Sans',sans-serif; font-size:1.8rem; font-weight:700; line-height:1; color:#0c1118; letter-spacing:.02em; }
    .np-stat-lbl { font-family:'DM Sans',sans-serif; font-size:10px; letter-spacing:.04em; color:#9ca3af; margin-top:2px; }
  `;
  document.head.appendChild(el);
}

function timeAgo(dateString) {
  const diff = Date.now() - new Date(dateString).getTime();
  const h = Math.floor(diff / 3600000), d = Math.floor(h / 24);
  if (h < 1)  return "Just now";
  if (h < 24) return `${h}h ago`;
  if (d < 7)  return `${d}d ago`;
  return new Date(dateString).toLocaleDateString();
}

function Pagination({ page, totalPages, pageSize, onPageChange, onPageSizeChange }) {
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  return (
    <div className="np-pagination">
      <div className="np-page-left">
        <div className="np-page-info">Page {page} of {totalPages}</div>
        <span className="np-page-size-label">Rows per page:</span>
        <select
          className="np-page-size-select"
          value={pageSize}
          onChange={e => onPageSizeChange(Number(e.target.value))}
        >
          {PAGE_SIZE_OPTIONS.map(n => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
      <div className="np-page-btns">
        <button className="np-page-btn" disabled={page === 1} onClick={() => onPageChange(page - 1)}>
          <ChevronLeft size={14} />
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} style={{ padding:"0 4px", color:"#9ca3af", fontSize:12 }}>…</span>
          ) : (
            <button
              key={p}
              className={`np-page-btn ${p === page ? "active" : ""}`}
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          )
        )}
        <button className="np-page-btn" disabled={page === totalPages} onClick={() => onPageChange(page + 1)}>
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

export default function NotificationsPage() {
  useEffect(() => { injectLocal(); }, []);

  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab,      setTab]      = useState("unread"); // "unread" | "read"
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(10);

  async function load() {
    setLoading(true);
    try { const { items } = await api.list({ onlyUnread: false, limit: 200 }); setRows(items); } catch {}
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  // Reset page when tab changes
  useEffect(() => { setPage(1); }, [tab]);

  async function markRead(id) {
    await api.markRead(id);
    setRows(xs => xs.map(x => x.id === id ? { ...x, isRead: true } : x));
    emit("notifications:changed", { type: "markRead", id, delta: -1 });
  }

  async function remove(id) {
    await api.remove(id);
    const wasUnread = rows.find(x => x.id === id)?.isRead === false;
    setRows(xs => xs.filter(x => x.id !== id));
    emit("notifications:changed", { type: "remove", id, delta: wasUnread ? -1 : 0 });
  }

  async function markAll() {
    await api.markAllRead();
    const unreadBefore = rows.filter(n => !n.isRead).length;
    setRows(xs => xs.map(x => ({ ...x, isRead: true })));
    emit("notifications:changed", { type: "markAllRead", delta: -unreadBefore });
  }

  const unreadCount = rows.filter(n => !n.isRead).length;
  const readCount   = rows.length - unreadCount;

  const filtered    = rows.filter(n => tab === "unread" ? !n.isRead : n.isRead);
  const totalPages  = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage    = Math.min(page, totalPages);
  const paginated   = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="np-wrap">
      {/* Header */}
      <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:"12px", marginBottom:"24px" }}>
        <div>
          <div className="up-title">Notifications</div>
          <div className="up-sub">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}` : "All caught up!"}
          </div>
        </div>
        <button className="up-btn up-btn-outline" onClick={() => (window.location.href = "/dashboard")}>
          <Home size={13} /> Dashboard
        </button>
      </div>

      {/* Action bar */}
      <div className="np-action-bar">
        <div className="np-action-left">
          <BellRing size={16} style={{ color:"#9ca3af" }} />
          <div className="np-action-count">{rows.length} total notifications</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button className="up-btn up-btn-outline" onClick={load} disabled={loading}>
            <RefreshCw size={13} style={{ animation: loading ? "upSpin .7s linear infinite" : "none" }} />
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <button className="up-btn up-btn-dark" onClick={markAll} disabled={unreadCount === 0}>
            <CheckCircle size={13} /> Mark All Read
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="np-tabs">
        <button className={`np-tab ${tab === "unread" ? "active" : ""}`} onClick={() => setTab("unread")}>
          <BellRing size={14} /> Unread
          <span className="np-tab-badge">{unreadCount}</span>
        </button>
        <button className={`np-tab ${tab === "read" ? "active" : ""}`} onClick={() => setTab("read")}>
          <CheckCircle size={14} /> Read
          <span className="np-tab-badge">{readCount}</span>
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="up-loading"><div className="up-spinner" /> Loading notifications…</div>
      ) : filtered.length === 0 ? (
        <div className="up-panel">
          <div className="up-empty">
            <Bell size={40} style={{ color:"#d1d5db", margin:"0 auto" }} />
            <div className="up-empty-title">
              {tab === "unread" ? "No unread notifications" : "No read notifications"}
            </div>
            <div className="up-empty-sub">
              {tab === "unread"
                ? "You're all caught up! No new notifications."
                : "Notifications you've read will appear here."}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="np-notif-scroll">
            <div className="np-notif-list">
              {paginated.map(n => (
                <div key={n.id} className={`np-notif ${n.isRead ? "" : "unread"}`}>
                  <div className={`np-dot ${n.isRead ? "read" : "unread"}`} />
                  <div className="np-notif-body">
                    <div className="np-notif-row">
                      <div style={{ flex:1, minWidth:0 }}>
                        <div className={`np-notif-title ${n.isRead ? "read" : ""}`}>{n.title}</div>
                        <div className="np-notif-text">{n.body}</div>
                        <div className="np-notif-meta">
                          <span style={{ display:"flex", alignItems:"center", gap:4 }}><Clock size={11} />{timeAgo(n.createdAt)}</span>
                          {n.linkUrl && <span style={{ display:"flex", alignItems:"center", gap:4 }}><ExternalLink size={11} /> Has link</span>}
                        </div>
                      </div>
                      <div className="np-notif-actions">
                        {n.linkUrl && (
                          <a href={n.linkUrl} className="np-act-btn link-btn" title="Open link"><ExternalLink size={15} /></a>
                        )}
                        {!n.isRead && (
                          <button className="np-act-btn read-btn" onClick={() => markRead(n.id)} title="Mark as read"><Eye size={15} /></button>
                        )}
                        <button className="np-act-btn del-btn" onClick={() => remove(n.id)} title="Delete"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Pagination
            page={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={size => { setPageSize(size); setPage(1); }}
          />
        </>
      )}

      {/* Stats */}
      {rows.length > 0 && (
        <div className="np-stats-grid">
          {[
            { icon: Bell,        iconBg:"rgba(99,102,241,.1)",   iconColor:"#6366f1", val: rows.length,     lbl:"Total"  },
            { icon: BellRing,    iconBg:"rgba(249,171,20,.1)",   iconColor:"#f9ab14", val: unreadCount,     lbl:"Unread" },
            { icon: CheckCircle, iconBg:"rgba(16,185,129,.1)",   iconColor:"#10b981", val: readCount,       lbl:"Read"   },
          ].map(k => (
            <div className="np-stat-card" key={k.lbl}>
              <div className="np-stat-icon" style={{ background: k.iconBg }}>
                <k.icon size={18} style={{ color: k.iconColor }} />
              </div>
              <div>
                <div className="np-stat-val">{k.val}</div>
                <div className="np-stat-lbl">{k.lbl}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
